import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Heart, Star, Phone, Mail, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Kosmetikstudio = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: Sparkles,
      title: "Gesichtsbehandlungen",
      description: "Professionelle Gesichtspflege, Peelings und Anti-Aging-Behandlungen für strahlende Haut.",
      price: "ab 45€"
    },
    {
      icon: Heart,
      title: "Massage & Wellness",
      description: "Entspannende Massagen und Wellness-Behandlungen für Körper und Seele.",
      price: "ab 55€"
    },
    {
      icon: Star,
      title: "Maniküre & Pediküre",
      description: "Professionelle Hand- und Fußpflege mit hochwertigen Produkten.",
      price: "ab 35€"
    },
    {
      icon: Sparkles,
      title: "Wimpern & Augenbrauen",
      description: "Wimpernverlängerung, Lash Lifting und professionelles Augenbrauen-Styling.",
      price: "ab 40€"
    }
  ];

  const features = [
    "Hochwertige Naturprodukte",
    "Individuelle Beratung",
    "Entspannte Atmosphäre",
    "Erfahrene Kosmetikerinnen"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-50">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/#portfolio" className="flex items-center gap-2 text-pink-600 hover:text-pink-700 transition-colors">
              <ArrowLeft size={20} />
              <span className="font-medium">Zurück zum Portfolio</span>
            </Link>
            <Button className="bg-pink-500 hover:bg-pink-600 text-white rounded-xl">
              Termin buchen
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-100 rounded-full text-pink-700 text-sm font-medium">
                <Sparkles size={16} />
                <span>Premium Kosmetikstudio</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 leading-tight">
                Ihre Schönheit, unsere Leidenschaft
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                Willkommen in unserem exklusiven Kosmetikstudio. Wir verwöhnen Sie mit professionellen Beauty-Behandlungen und hochwertigen Produkten für Ihr Wohlbefinden.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-pink-500 hover:bg-pink-600 text-white rounded-xl shadow-lg">
                  Termin vereinbaren
                </Button>
                <Button size="lg" variant="outline" className="border-2 border-pink-300 text-slate-700 hover:bg-pink-50 rounded-xl">
                  Unsere Preise
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1611169035510-f9af52e6dbe2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxiZWF1dHklMjBzYWxvbiUyMHNwYXxlbnwwfHx8fDE3NzM1Nzk2Njh8MA&ixlib=rb-4.1.0&q=85"
                alt="Spa Treatment"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Unsere Leistungen
            </h2>
            <div className="w-20 h-1 bg-pink-500 mx-auto mb-6 rounded-full"></div>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Von der klassischen Gesichtsbehandlung bis zur entspannenden Massage - wir bieten alles für Ihre Schönheit
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-pink-100 hover:shadow-xl transition-all rounded-2xl">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-pink-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Icon className="text-pink-600" size={28} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-slate-800 mb-2">{service.title}</h3>
                        <p className="text-slate-600 mb-3">{service.description}</p>
                        <div className="text-pink-600 font-semibold">{service.price}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-pink-50 to-rose-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Studio-Atmosphäre
            </h2>
            <div className="w-20 h-1 bg-pink-500 mx-auto rounded-full"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <img
              src="https://images.unsplash.com/photo-1559185590-d545a0c5a1dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxiZWF1dHklMjBzYWxvbiUyMHNwYXxlbnwwfHx8fDE3NzM1Nzk2Njh8MA&ixlib=rb-4.1.0&q=85"
              alt="Spa Atmosphere"
              className="rounded-2xl shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform"
            />
            <img
              src="https://images.unsplash.com/photo-1559185590-fcf099ac62c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxiZWF1dHklMjBzYWxvbiUyMHNwYXxlbnwwfHx8fDE3NzM1Nzk2Njh8MA&ixlib=rb-4.1.0&q=85"
              alt="Treatment Room"
              className="rounded-2xl shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform"
            />
            <img
              src="https://images.unsplash.com/photo-1605980626247-eb3a2f10ec8f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHxiZWF1dHklMjBzYWxvbiUyMHNwYXxlbnwwfHx8fDE3NzM1Nzk2Njh8MA&ixlib=rb-4.1.0&q=85"
              alt="Hair Treatment"
              className="rounded-2xl shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6">
                Warum uns wählen?
              </h2>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Star className="text-white" size={14} />
                    </div>
                    <span className="text-slate-700 text-lg">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <Card className="border-pink-100 rounded-2xl shadow-xl">
              <CardContent className="pt-6 space-y-6">
                <h3 className="text-2xl font-bold text-slate-800">Öffnungszeiten</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Clock className="text-pink-600" size={20} />
                    <div>
                      <div className="font-semibold text-slate-800">Mo - Fr</div>
                      <div className="text-slate-600">9:00 - 19:00 Uhr</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="text-pink-600" size={20} />
                    <div>
                      <div className="font-semibold text-slate-800">Samstag</div>
                      <div className="text-slate-600">10:00 - 16:00 Uhr</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="text-pink-600" size={20} />
                    <div>
                      <div className="font-semibold text-slate-800">Sonntag</div>
                      <div className="text-slate-600">Geschlossen</div>
                    </div>
                  </div>
                </div>
                <Button className="w-full bg-pink-500 hover:bg-pink-600 text-white rounded-xl">
                  Jetzt Termin buchen
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-pink-50 to-rose-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6">
            Vereinbaren Sie einen Termin
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Gönnen Sie sich eine Auszeit und lassen Sie sich von uns verwöhnen
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="border-pink-100 rounded-2xl">
              <CardContent className="pt-6 text-center">
                <Phone className="text-pink-600 mx-auto mb-3" size={32} />
                <div className="font-semibold text-slate-800 mb-1">Telefon</div>
                <div className="text-slate-600">+49 (0) 6107 234567</div>
              </CardContent>
            </Card>
            <Card className="border-pink-100 rounded-2xl">
              <CardContent className="pt-6 text-center">
                <Mail className="text-pink-600 mx-auto mb-3" size={32} />
                <div className="font-semibold text-slate-800 mb-1">E-Mail</div>
                <div className="text-slate-600">info@beauty-studio.de</div>
              </CardContent>
            </Card>
            <Card className="border-pink-100 rounded-2xl">
              <CardContent className="pt-6 text-center">
                <MapPin className="text-pink-600 mx-auto mb-3" size={32} />
                <div className="font-semibold text-slate-800 mb-1">Adresse</div>
                <div className="text-slate-600">Hauptstraße 45, Kelsterbach</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-slate-300">
            © 2025 Beauty & Wellness Studio • Erstellt von{' '}
            <Link to="/" className="text-pink-400 hover:text-pink-300">
              WebWerk Kelsterbach
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Kosmetikstudio;
